import { useMemo } from 'react';

import { useParam } from './useParam';

type UsePaginatedParams<T> = {
  nodes?: T[];

  totalItems?: number;
  initialPage?: number;
  initialPageSize?: number;
  withQueryParams?: boolean;

  storageKey?: string;
  storageType?: 'session' | 'local';
};

export type UsePaginatedResult<T> = {
  pagination: Pagination;

  setPage: (page: number) => void;
  setPageSize: (page: number) => void;
  setPaginationChange: (params: PageParams) => void;

  nodes: T[];
};

type PageParams = {
  page?: number;
  pageSize?: number;
};

export type Pagination = {
  page: number;
  totalPages: number;
  perPage: number;
  totalItems: number;

  limit: number;
  offset: number;
};

export const usePaginate = <T>(
  nodes: T[] | undefined,
  pagination: Pagination
) => {
  return useMemo(() => {
    if (!nodes?.length) return [];

    const { offset, limit } = pagination;
    return nodes.slice(offset, offset + limit);
  }, [nodes, pagination]);
};

export const usePaginated = <T>(
  config?: UsePaginatedParams<T>
): UsePaginatedResult<T> => {
  const [page, setPage] = useParam({
    name: 'page',
    enableUrl: !!config?.withQueryParams,
    defaultValue: config?.initialPage ?? 1,

    storageType: config?.storageType,
    storageKey: config?.storageKey ? `${config?.storageKey}.page` : undefined,
  });
  const [pageSize, setPageSize] = useParam({
    name: 'pageSize',
    enableUrl: !!config?.withQueryParams,
    defaultValue: config?.initialPageSize ?? 50,

    storageType: config?.storageType,
    storageKey: config?.storageKey
      ? `${config?.storageKey}.pageSize`
      : undefined,
  });

  const totalItems = config?.totalItems ?? 0;
  const pagination: Pagination = useMemo(
    () => ({
      page: page || 0,
      perPage: pageSize || 0,
      totalItems: totalItems,
      totalPages: Math.ceil(totalItems / (pageSize || 1)),

      limit: pageSize || 1,
      offset: ((page || 1) - 1) * (pageSize || 1),
    }),
    [totalItems, page, pageSize]
  );

  const setPaginationChange = (params: PageParams) => {
    if (params.page !== undefined) {
      setPage(params.page);
    }
    if (params.pageSize !== undefined) {
      setPageSize?.(params.pageSize);
    }
  };

  const nodes = usePaginate(config?.nodes, pagination);

  return {
    pagination,

    setPage,
    setPageSize,
    setPaginationChange,

    nodes,
  };
};
